import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import {
  FileDescriptorRead,
  PublicationRead,
  PublicationVersionFile,
  PublicationVersionRead,
} from "@nens/rana/api-v1";

import { useFileDescriptorsQuery } from "@/queries/query-file-descriptors";
import {
  getPublicationVersionFilesQuery,
  usePublicationVersionFilesQuery,
} from "@/queries/query-publication-version-files";
import { getPublicationVersionQuery } from "@/queries/query-version";

import { useSetVersion } from "./use-set-version";

export type PublicationVersionData = {
  publicationVersion: PublicationVersionRead;
  files: PublicationVersionFile[];
  descriptors: FileDescriptorRead[];
};

export function usePublicationVersion(publicationRead: PublicationRead) {
  const queryClient = useQueryClient();

  const [resetCounter, _setResetCounter] = useState<number>(0);

  const resetPublicationVersion = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getPublicationVersionQuery(publicationRead.id, undefined, "latest").queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: getPublicationVersionFilesQuery(publicationRead.id, undefined).queryKey.slice(
          0,
          2
        ),
      }),
    ]);

    _setResetCounter((counter) => counter + 1);
  }, [publicationRead.id, queryClient]);

  const { version, setPublicationVersion, setLatestVersion, latestVersion } =
    useSetVersion(publicationRead);
  const [resetPublicationModalOpen, setResetPublicationModalOpen] = useState(false);

  const pvQuery = useQuery(
    getPublicationVersionQuery(
      publicationRead.id,
      publicationRead.publishedVersion ?? undefined,
      version
    )
  );

  const actualVersion = pvQuery.data?.version;

  // Files are fetched separately; enabled is false when version is 0 (newly created, no saves yet)
  const filesQuery = usePublicationVersionFilesQuery(publicationRead.id, actualVersion);

  const descriptorIds = filesQuery.data?.map((f) => f.file.descriptorId) ?? [];
  const descriptorQueries = useFileDescriptorsQuery(descriptorIds);

  const allDescriptorsReady =
    descriptorIds.length === 0 || descriptorQueries.every((q) => q.data !== undefined);

  // Memoize combined with useQueries doesn't work, so use a ids string array
  const allDescriptors = useMemo(
    () => descriptorQueries.flatMap((q) => (q.data ? [q.data] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(descriptorQueries.map((q) => q.data))]
  );

  // Only expose combined data once all three layers have resolved
  const publicationVersion: PublicationVersionData | undefined = useMemo(() => {
    if (
      !pvQuery.data ||
      (actualVersion !== 0 && filesQuery.data === undefined) ||
      !allDescriptorsReady
    ) {
      return undefined;
    }
    return {
      publicationVersion: pvQuery.data,
      files: filesQuery.data ?? [],
      descriptors: allDescriptors,
    };
  }, [pvQuery.data, filesQuery.data, allDescriptors, allDescriptorsReady, actualVersion]);

  if (pvQuery.data && version === "latest" && latestVersion !== pvQuery.data.version) {
    setLatestVersion(pvQuery.data.version);
  }

  return {
    isLoading:
      pvQuery.isLoading ||
      filesQuery.isLoading ||
      (descriptorIds.length > 0 && descriptorQueries.some((q) => q.isLoading)),
    publicationVersion,
    setPublicationVersion,
    latestVersion,

    resetCounter,
    resetPublicationVersion,
    resetPublicationModalOpen,
    setResetPublicationModalOpen,
  };
}
